import { useEffect, useMemo, useState } from 'react';

import moment from 'moment';

import { UserApi } from '@/api';
import { SelectItem } from '@/components';
import { DATE_TIME_FORMAT } from '@/config';
import { IUser } from '@/models';
import { useToastStore, useUserStore } from '@/store';

export const getCustodyActionDefaultValues = (user?: IUser | null) => ({
  actionDateTime: moment().format(DATE_TIME_FORMAT),
  officer: user?._id || '',
});

export const normalizeCustodyActionDateTime = (value?: string) => {
  if (!value) return value;

  const localDateTime = moment(value, DATE_TIME_FORMAT, true);
  if (localDateTime.isValid()) {
    return localDateTime.toISOString();
  }

  const fallbackDateTime = moment(value);
  return fallbackDateTime.isValid() ? fallbackDateTime.toISOString() : value;
};

export const useCustodyOfficerOptions = () => {
  const { user } = useUserStore();
  const { updateToast } = useToastStore();
  const [agencyUsers, setAgencyUsers] = useState<IUser[]>([]);

  useEffect(() => {
    const fetchAgencyUsers = async () => {
      try {
        const res = await UserApi.get({ limit: 1000 });
        setAgencyUsers(res.data.results);
      } catch (err: any) {
        updateToast({ open: true, message: err.message });
      }
    };

    fetchAgencyUsers();
  }, [updateToast]);

  const officerOptions = useMemo(() => {
    const sortedUsers = [...agencyUsers].sort((a, b) => {
      if (a._id === user?._id) return -1;
      if (b._id === user?._id) return 1;
      return (a.fullName || a.email || '').localeCompare(
        b.fullName || b.email || '',
      );
    });

    return sortedUsers.map(({ _id, fullName, email }) => ({
      label: fullName || email,
      value: _id || '',
    })) as SelectItem[];
  }, [agencyUsers, user?._id]);

  return { officerOptions, currentUser: user };
};
